import { useRef } from "react";
import { useInView } from "framer-motion";
import { T } from "@/constants/testIds";
import { LINKS } from "@/constants/links";
import { useCountUp } from "@/hooks/useStats";
import { fmtUsd, fmtInt } from "@/lib/format";
import BurnCard from "@/components/BurnCard";
import Reveal from "@/components/Reveal";

function Stat({ label, value, format, joke, testid, delay = 0, extra }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  const n = useCountUp(value, inView);
  const display = n == null ? "—" : format === "usd" ? fmtUsd(n) : fmtInt(n);
  const size = display.length > 8 ? "text-xl md:text-2xl" : "text-3xl md:text-4xl";

  return (
    <Reveal delay={delay} className="h-full">
      <div ref={ref} data-testid={testid} className="card p-6 md:p-7 h-full flex flex-col justify-between">
        <div className="kicker text-[color:var(--muted)]">{label}</div>
        <div className={`font-display tabular ${size} leading-none text-paper mt-6 whitespace-nowrap`}>
          {display}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="text-sm text-paper/55">{joke}</span>
          {extra}
        </div>
      </div>
    </Reveal>
  );
}

export default function LiveStats({ stats, loading, error }) {
  const change = stats?.price_change_24h;
  const buys = stats?.txns_24h_buys;
  const sells = stats?.txns_24h_sells;

  return (
    <section id="stats" className="relative bg-ink text-paper py-20 md:py-28" data-testid={T.stats}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="kicker text-lime mb-4 flex items-center gap-2">
              <span className="live-dot" />
              {loading ? "sniffing the chain…" : error ? "chain is napping · showing last known" : "live · refreshes every 60s"}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9]">
              Numbers that go up.
              <br />
              <span className="text-outline-lime">Sometimes.</span>
            </h2>
          </div>
          <p className="max-w-sm text-base text-paper/60">
            Burn is read straight from the chain: we ask the contract how much sits in the dead
            wallet. No spreadsheet. No trust. Just a dog and an RPC call.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          <Reveal className="lg:col-span-7">
            <BurnCard stats={stats} />
          </Reveal>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            <Stat
              label="Market cap"
              value={stats?.market_cap}
              format="usd"
              joke="Smaller than a hoodie brand. Bigger than your ex's."
              testid={T.statMarketCap}
              delay={0.05}
            />
            <Stat
              label="24h volume"
              value={stats?.volume_24h}
              format="usd"
              joke="Barks per day, in USD."
              testid={T.statVolume}
              delay={0.1}
            />
            <Stat
              label="Price"
              value={stats?.price_usd}
              format="usd"
              joke="Per dog."
              testid={T.statPrice}
              delay={0.15}
              extra={
                typeof change === "number" && (
                  <span
                    className={
                      "font-mono text-xs px-2 py-1 rounded-full border " +
                      (change >= 0
                        ? "text-lime border-[color:var(--lime)]/50"
                        : "text-[#ff7a4d] border-[#ff7a4d]/50")
                    }
                  >
                    {change >= 0 ? "+" : ""}
                    {change.toFixed(2)}%
                  </span>
                )
              }
            />
            <Stat
              label="Liquidity"
              value={stats?.liquidity_usd}
              format="usd"
              joke="The kibble bowl."
              testid={T.statLiquidity}
              delay={0.2}
            />
          </div>
        </div>

        <Reveal delay={0.1} className="mt-4 md:mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          <div data-testid={T.statTxns} className="card p-6 flex items-center justify-between">
            <div>
              <div className="kicker text-[color:var(--muted)] mb-2">24h buys / sells</div>
              <div className="font-display text-2xl tabular">
                <span className="text-lime">{fmtInt(buys)}</span>
                <span className="text-paper/30 mx-2">/</span>
                <span className="text-[#ff7a4d]">{fmtInt(sells)}</span>
              </div>
            </div>
            <span className="text-sm text-paper/50 max-w-[9rem] text-right">
              {buys != null && sells != null && buys >= sells ? "More paws in than out." : "Paper paws detected."}
            </span>
          </div>
          <div data-testid={T.statHolders} className="card p-6 flex items-center justify-between">
            <div>
              <div className="kicker text-[color:var(--muted)] mb-2">Hooded holders</div>
              <div className="font-display text-2xl tabular">
                {stats?.holders_count != null ? fmtInt(stats.holders_count) : "many"}
              </div>
            </div>
            <span className="text-sm text-paper/50 max-w-[9rem] text-right">All wearing the same hoodie.</span>
          </div>
          <a
            href={stats?.pair_url || LINKS.dexscreener}
            target="_blank"
            rel="noreferrer"
            className="card p-6 flex items-center justify-between hover:!border-[color:var(--lime)]"
          >
            <div>
              <div className="kicker text-[color:var(--muted)] mb-2">Chart</div>
              <div className="font-display text-2xl">DexScreener ↗</div>
            </div>
            <span className="text-sm text-paper/50 max-w-[9rem] text-right">He doesn't look. You can.</span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}
